import * as Dialog from "@radix-ui/react-dialog"
import { ChevronLeft, ChevronRight, Copy, Download, Loader2, X } from "lucide-react"
import { useEffect, useRef, useState, type CSSProperties } from "react"
import { toast } from "sonner"
import { SingleClipPlayer } from "@/components/timeline-player"
import { useTranslation } from "@/hooks/use-translation"
import { dialogOverlayProps } from "@/lib/dialog-layer"
import { copyImageToClipboard, downloadImage, filenameFromUrl } from "@/lib/download-image"
import { cn } from "@/lib/utils"

export type ImageLightboxDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  url: string | null
  /** When set, renders a video player instead of an image. */
  videoUrl?: string | null
  /** View-only audio. Takes the stage ahead of video and image. */
  audioUrl?: string | null
  alt?: string | null
  title?: string | null
  description?: string | null
  /** When set, shows download (and copy, for an image) plus the original's resolution. */
  downloadUrl?: string | null
  downloadFilename?: string | null
  copyable?: boolean
  /**
   * Raise z-index above fullscreen hosts like ShotPanel (z 100/101).
   * Pass 2 when opening above a nested (z 110/111) dialog, e.g. the frame library picker.
   * Pass 3 above immersive 3D scene stage (z 140) / scene reference picker.
   */
  nested?: boolean | 2 | 3
  /** Canvas behind the image (e.g. #C0C0C0 for prop front-view). */
  imageBackgroundColor?: string
  /** Shrink image to 80% and center for equal margins. */
  imagePadded?: boolean
  /** Player source id. Defaults to 1 when the caller has no version id. */
  videoSourceId?: number
  videoDurationMs?: number | null
  videoFrameRate?: number | null
  videoAspectRatio?: number | null
  videoTimelineFrameUrls?: readonly string[]
  /** Play this window of the file (animatic segment). Without it the whole file plays. */
  videoSourceInMs?: number | null
  videoSourceOutMs?: number | null
  /** Gallery paging. Buttons and arrows appear only when a callback is set. */
  onPrev?: () => void
  onNext?: () => void
  hasPrev?: boolean
  hasNext?: boolean
  prevLabel?: string
  nextLabel?: string
  /** Shown when the open item has no media url. Defaults to the image empty state. */
  emptyMessage?: string | null
  onOpenAutoFocus?: (event: Event) => void
  onCloseAutoFocus?: (event: Event) => void
}

function lightboxOverlayZ(nested: boolean | 2 | 3): string {
  if (nested === 3) return "z-[150]"
  if (nested === 2) return "z-[112]"
  if (nested) return "z-[110]"
  return "z-[100]"
}

function lightboxContentZ(nested: boolean | 2 | 3): string {
  if (nested === 3) return "z-[151]"
  if (nested === 2) return "z-[113]"
  if (nested) return "z-[111]"
  return "z-[101]"
}

const headerToolClass =
  "inline-flex items-center gap-1.5 rounded-lg border border-ui-border bg-ui-surface-raised px-2.5 py-1.5 text-xs text-ui-text-primary transition-colors hover:bg-ui-surface-raised disabled:opacity-40"

export function ImageLightboxDialog({
  open,
  onOpenChange,
  url,
  videoUrl,
  audioUrl,
  alt,
  title,
  description,
  downloadUrl,
  downloadFilename,
  copyable,
  nested = false,
  imageBackgroundColor,
  imagePadded = false,
  videoSourceId = 1,
  videoDurationMs = null,
  videoFrameRate = null,
  videoAspectRatio = null,
  videoTimelineFrameUrls,
  videoSourceInMs = null,
  videoSourceOutMs = null,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  prevLabel,
  nextLabel,
  emptyMessage,
  onOpenAutoFocus,
  onCloseAutoFocus,
}: ImageLightboxDialogProps) {
  const { t } = useTranslation(["common"])
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null)
  const [imageLoading, setImageLoading] = useState(false)
  const [copying, setCopying] = useState(false)
  // The player takes horizontal arrows only after a click on it; a click outside pages again.
  const [playerEngaged, setPlayerEngaged] = useState(false)
  const imageRef = useRef<HTMLImageElement>(null)
  const playerRef = useRef<HTMLDivElement>(null)
  const isAudio = Boolean(audioUrl)
  const isVideo = Boolean(videoUrl) && !isAudio
  const navigable = Boolean(onPrev || onNext)

  useEffect(() => {
    if (!open) {
      setNaturalSize(null)
      setImageLoading(false)
      setPlayerEngaged(false)
      return
    }
    if (url && !isVideo && !isAudio) {
      setImageLoading(true)
    }
  }, [open, url, isVideo, isAudio])

  useEffect(() => {
    setPlayerEngaged(false)
  }, [url, videoUrl, audioUrl])

  useEffect(() => {
    const img = imageRef.current
    if (!open || !url || isVideo || isAudio || !img?.complete) return
    setImageLoading(false)
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight })
  }, [open, url, isVideo, isAudio])

  useEffect(() => {
    if (!open || !navigable) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      setPlayerEngaged(playerRef.current != null && target != null && playerRef.current.contains(target))
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => document.removeEventListener("pointerdown", onPointerDown, true)
  }, [open, navigable])

  useEffect(() => {
    if (!open || !navigable) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
      const horizontal = event.key === "ArrowLeft" || event.key === "ArrowRight"
      // Video and audio own horizontal arrows only after a click on the player.
      // Otherwise a native <audio> could not be seeked: arrows would only page.
      if (horizontal && playerEngaged && (isVideo || isAudio)) return
      const forward = event.key === "ArrowRight" || event.key === "ArrowDown"
      const backward = event.key === "ArrowLeft" || event.key === "ArrowUp"
      if (!forward && !backward) return
      event.preventDefault()
      event.stopPropagation()
      if (forward) onNext?.()
      else onPrev?.()
    }
    document.addEventListener("keydown", onKeyDown, true)
    return () => document.removeEventListener("keydown", onKeyDown, true)
  }, [open, navigable, playerEngaged, isVideo, isAudio, onNext, onPrev])

  const showTools = Boolean(downloadUrl)
  const canCopy = Boolean(copyable) && !isVideo && !isAudio
  const imageStageStyle: CSSProperties | undefined = imageBackgroundColor
    ? { backgroundColor: imageBackgroundColor }
    : undefined
  const stageMin = navigable ? "min-h-[70vh]" : "min-h-0"

  const handleDownload = () => {
    if (!downloadUrl) return
    void downloadImage(downloadUrl, downloadFilename ?? filenameFromUrl(downloadUrl, "original"))
  }

  const handleCopy = async () => {
    if (!downloadUrl || copying) return
    setCopying(true)
    try {
      await copyImageToClipboard(downloadUrl)
      toast.success(t("imageLightbox.copySuccess"))
    } catch {
      toast.error(t("imageLightbox.copyError"))
    } finally {
      setCopying(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          {...dialogOverlayProps}
          className={cn(
            "ui-dialog-overlay fixed inset-0 bg-[var(--ui-overlay-heavy)] backdrop-blur-sm",
            lightboxOverlayZ(nested),
          )}
        />
        <Dialog.Content
          className={cn(
            open && "ui-dialog-content",
            "fixed top-1/2 left-1/2 flex max-h-[95vh] w-[min(96vw,1280px)] -translate-x-1/2 -translate-y-1/2 flex-col",
            "rounded-lg border border-ui-border p-3 shadow-2xl shadow-black/60 focus:outline-none lg:p-4 dark:bg-ui-bg-elevated light:bg-ui-surface",
            lightboxContentZ(nested),
          )}
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <div className="flex items-start justify-between gap-3 pb-2">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-base font-semibold text-ui-text-primary">
                {title ?? t("imageLightbox.defaultTitle")}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-0.5 truncate text-xs text-ui-text-secondary">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {navigable ? (
                <>
                  <button
                    type="button"
                    onClick={onPrev}
                    disabled={!hasPrev}
                    className={headerToolClass}
                    aria-label={prevLabel}
                    title={prevLabel}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={onNext}
                    disabled={!hasNext}
                    className={headerToolClass}
                    aria-label={nextLabel}
                    title={nextLabel}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              ) : null}
              {showTools && (
                <>
                  {naturalSize && !isVideo && !isAudio ? (
                    <span className="hidden text-xs text-ui-text-secondary tabular-nums sm:inline">
                      {naturalSize.w}&times;{naturalSize.h}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleDownload}
                    className={headerToolClass}
                    title={t("imageLightbox.downloadOriginal")}
                  >
                    <Download className="h-4 w-4" />
                    {t("imageLightbox.downloadOriginal")}
                  </button>
                  {canCopy ? (
                    <button
                      type="button"
                      onClick={() => void handleCopy()}
                      disabled={copying}
                      className={headerToolClass}
                      title={t("imageLightbox.copyTitle")}
                    >
                      {copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                      {t("imageLightbox.copy")}
                    </button>
                  ) : null}
                </>
              )}
              <Dialog.Close
                type="button"
                className="shrink-0 rounded-lg p-1.5 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
                aria-label={t("close")}
              >
                <X className="h-5 w-5" />
              </Dialog.Close>
            </div>
          </div>
          {isAudio && audioUrl ? (
            <div
              className={cn(
                "flex flex-1 items-center justify-center rounded-lg p-2 dark:bg-black/60 light:bg-clay-2",
                stageMin,
              )}
            >
              <div ref={playerRef} className="mx-auto w-full max-w-xl">
                <audio key={audioUrl} src={audioUrl} controls autoPlay className="w-full" />
              </div>
            </div>
          ) : null}
          {isVideo && videoUrl ? (
            <div
              className={cn(
                "it2tv-scrollbar flex-1 overflow-y-auto rounded-lg p-2 dark:bg-black/60 light:bg-clay-2",
                stageMin,
              )}
            >
              <div ref={playerRef} className="w-full">
                <SingleClipPlayer
                  key={`${videoUrl}:${videoSourceInMs ?? ""}:${videoSourceOutMs ?? ""}`}
                  sourceId={videoSourceId}
                  streamUrl={videoUrl}
                  durationMs={videoDurationMs}
                  frameRate={videoFrameRate}
                  aspectRatio={videoAspectRatio}
                  timelineFrameUrls={videoTimelineFrameUrls}
                  sourceInMs={videoSourceInMs}
                  sourceOutMs={videoSourceOutMs}
                  active={open}
                  autoPlay
                  showTrackHeaders={false}
                  className="w-full"
                />
              </div>
            </div>
          ) : null}
          {!isAudio && !isVideo ? (
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className={cn(
                "relative flex flex-1 items-center justify-center overflow-hidden rounded-lg p-2",
                navigable ? "min-h-[70vh]" : "min-h-[min(42vh,380px)]",
                !imageBackgroundColor && "dark:bg-black/60 light:bg-clay-2",
              )}
              style={imageStageStyle}
              title={t("close")}
            >
              {url ? (
                <>
                  <img
                    ref={imageRef}
                    src={url}
                    alt={alt ?? ""}
                    onLoad={(e) => {
                      setImageLoading(false)
                      setNaturalSize({
                        w: e.currentTarget.naturalWidth,
                        h: e.currentTarget.naturalHeight,
                      })
                    }}
                    onError={() => setImageLoading(false)}
                    className={cn(
                      "object-contain",
                      imagePadded ? "h-[80%] max-h-[82vh] w-[80%] max-w-full" : "max-h-[82vh] w-auto max-w-full",
                      imageLoading && "opacity-40",
                    )}
                  />
                  {imageLoading ? (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-ui-text-muted" />
                    </span>
                  ) : null}
                </>
              ) : (
                <div className="flex min-h-[200px] items-center justify-center text-sm text-ui-text-muted">
                  {emptyMessage ?? t("imageLightbox.noImage")}
                </div>
              )}
            </button>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
